/**
 * src/context/AuthContext.tsx
 * Authentication context managing edge JWT session and user profile
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export interface UserProfile {
  id: number;
  username: string;
  full_name?: string | null;
  whatsapp_phone?: string | null;
  role: 'admin' | 'user';
  current_balance: number;
  avatar_url?: string | null;
  upi_id?: string | null;
  created_at?: string;
}

interface AuthContextType {
  user: UserProfile | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('ckaudit_jwt'));
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch current user from /api/auth/me on mount or token change
  const refreshUser = useCallback(async () => {
    const currentToken = localStorage.getItem('ckaudit_jwt');
    if (!currentToken) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      });

      if (res.ok) {
        const json = (await res.json()) as any;
        if (json.success && json.data?.user) {
          setUser(json.data.user);
        } else {
          // Token invalid
          localStorage.removeItem('ckaudit_jwt');
          setToken(null);
          setUser(null);
        }
      } else {
        localStorage.removeItem('ckaudit_jwt');
        setToken(null);
        setUser(null);
      }
    } catch (err) {
      console.warn('Could not connect to /api/auth/me, using cached session if available:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    const handleUnauthorized = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  const login = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const json = (await res.json()) as any;
      if (res.ok && json.success) {
        const receivedToken = json.data.token;
        const loggedInUser = json.data.user;

        localStorage.setItem('ckaudit_jwt', receivedToken);
        setToken(receivedToken);
        setUser(loggedInUser);
        return { success: true };
      } else {
        return { success: false, error: json.error || 'Login failed. Please check credentials.' };
      }
    } catch (err: any) {
      return { success: false, error: 'Network error connecting to edge server. ' + err.message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch (e) {
      // Ignore network errors on logout
    }
    localStorage.removeItem('ckaudit_jwt');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      isLoading,
      isAuthenticated: !!user,
      login,
      logout,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
